import React, { useEffect } from "react";
import { Printer, ArrowLeft, MapPin, Wifi, ShieldCheck, Dumbbell, BookOpen, Bike, Zap, Coffee, Users, HeartHandshake } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";

const principles = [
  { title: "Quality Accommodation", body: "Modern, purpose-built rooms with everything a student needs to focus and thrive." },
  { title: "Convenient Locations", body: "Steps from campus, shops, transport and the neighbourhoods students love." },
  { title: "Safety & Security", body: "24/7 access-controlled buildings with CCTV, biometrics and on-site management." },
  { title: "Health & Wellbeing", body: "Clean, well-maintained spaces designed to support academic and personal wellness." },
  { title: "Affordability", body: "Competitive all-inclusive rates and flexible options — NSFAS support where available." },
  { title: "Great Student Experiences", body: "Communities, events and study spaces that make university years unforgettable." },
];

const amenities = [
  { icon: Wifi, label: "Uncapped WiFi" },
  { icon: ShieldCheck, label: "24/7 Security" },
  { icon: BookOpen, label: "Study Lounges" },
  { icon: Dumbbell, label: "On-site Gym" },
  { icon: Zap, label: "Backup Power" },
  { icon: Bike, label: "Bike Storage" },
  { icon: Coffee, label: "Common Rooms" },
  { icon: Users, label: "Community Events" },
];

const cities = [
  { name: "Johannesburg", tag: "Braamfontein · Auckland Park · Rosebank", img: "https://images.unsplash.com/photo-1577086664693-894d8405334a?w=1000&auto=format&fit=crop&q=80" },
  { name: "Pretoria", tag: "Hatfield · Sunnyside", img: "https://images.unsplash.com/photo-1580746738099-1efe74d0148f?w=1000&auto=format&fit=crop&q=80" },
  { name: "Cape Town", tag: "Observatory · Rondebosch · CBD", img: "https://images.unsplash.com/photo-1516026672322-bc52d61a55d5?w=1000&auto=format&fit=crop&q=80" },
  { name: "Durban", tag: "Berea · Musgrave", img: "https://images.unsplash.com/photo-1518729571562-90a3f42fa9d8?w=1000&auto=format&fit=crop&q=80" },
];

export default function Brochure() {
  useEffect(() => {
    document.title = "Campusco — Live. Study. Thrive.";
  }, []);

  return (
    <div className="bg-background text-foreground min-h-screen">
      {/* Toolbar */}
      <div className="no-print bg-primary text-primary-foreground sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm hover:text-secondary" data-testid="brochure-back">
            <ArrowLeft className="w-4 h-4" /> Back to site
          </Link>
          <Button onClick={() => window.print()} className="rounded-sm bg-secondary text-secondary-foreground hover:bg-secondary/90" data-testid="brochure-print-btn">
            <Printer className="w-4 h-4 mr-2" /> Print / Save as PDF
          </Button>
        </div>
      </div>

      {/* ============ COVER ============ */}
      <section className="relative min-h-[92vh] print:min-h-[95vh] flex items-end overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=1920&auto=format&fit=crop&q=80"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/60 to-primary/10" />
        <div className="relative max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-24 text-primary-foreground w-full">
          <div className="flex justify-between items-start mb-16">
            <Logo variant="light" className="h-14 w-auto" />
            <p className="overline text-primary-foreground/70 text-xs">Student Living · Since 2017</p>
          </div>
          <p className="overline text-secondary">Campusco Student Living</p>
          <h1 className="mt-4 font-serif text-5xl md:text-7xl lg:text-8xl leading-[0.95] max-w-4xl">
            Live. Study. <em className="italic text-secondary font-normal">Thrive.</em>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-primary-foreground/90 leading-relaxed">
            Modern student accommodation across South Africa — purpose-built,
            professionally managed, and designed for the way students actually live.
          </p>
        </div>
      </section>

      {/* ============ INTRO ============ */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-20 border-b border-border page-break">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="overline text-secondary">Rise to Your Potential</p>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl leading-tight">
              Student housing built <em className="italic text-secondary font-normal">for students</em> — not for landlords.
            </h2>
            <div className="mt-6 space-y-4 text-muted-foreground leading-relaxed">
              <p>
                At Campusco, our residences are designed exclusively for student living.
                From your first move-in day to graduation, everything you need is on-site
                or steps away — modern suites, dedicated management, and communities
                where students actually thrive.
              </p>
              <p>
                We&apos;re a proudly South African, woman-led property firm. Since 2017,
                we&apos;ve been quietly building a reputation for buildings that stay full,
                stay safe, and stay memorable — for all the right reasons.
              </p>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=900&auto=format&fit=crop&q=80"
                alt="Students on campus"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ============ 6 PRINCIPLES ============ */}
      <section className="bg-muted/40 border-b border-border py-20 page-break">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="flex items-baseline justify-between flex-wrap gap-6">
            <div>
              <p className="overline text-secondary">Our Philosophy</p>
              <h2 className="mt-3 font-serif text-4xl md:text-5xl">Six principles. Zero compromise.</h2>
            </div>
            <p className="text-sm text-muted-foreground max-w-md">
              Every Campusco residence is built on the same foundation, whether it&apos;s
              a 24-room building in Braamfontein or a 220-bed development in Hatfield.
            </p>
          </div>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {principles.map((p, i) => (
              <div key={p.title} className="bg-background border border-border p-8">
                <span className="font-serif text-2xl text-secondary">0{i + 1}</span>
                <h3 className="mt-4 font-serif text-2xl">{p.title}</h3>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ AMENITIES ============ */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-20 border-b border-border page-break">
        <p className="overline text-secondary">Everything Included</p>
        <h2 className="mt-3 font-serif text-4xl md:text-5xl max-w-2xl">Amenities that make student life easier.</h2>
        <p className="mt-6 text-muted-foreground max-w-3xl">
          Your rent doesn&apos;t just cover a room. It covers the whole experience —
          from load-shedding-proof WiFi to spaces where you can study, work out,
          hang out and actually enjoy your time at university.
        </p>

        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-px bg-border border border-border">
          {amenities.map((a) => {
            const Icon = a.icon;
            return (
              <div key={a.label} className="bg-background p-6 flex flex-col items-center text-center gap-3">
                <Icon className="w-8 h-8 text-secondary" strokeWidth={1.5} />
                <p className="font-serif text-lg">{a.label}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ============ LOCATIONS ============ */}
      <section className="bg-primary text-primary-foreground py-20 page-break">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="flex items-baseline justify-between flex-wrap gap-6">
            <div>
              <p className="overline text-secondary">Where We Are</p>
              <h2 className="mt-3 font-serif text-4xl md:text-5xl">Steps away from every major campus.</h2>
            </div>
            <p className="text-sm text-primary-foreground/70 max-w-md">
              Our residences are located in the neighbourhoods students already want to live in.
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {cities.map((c) => (
              <div key={c.name} className="group">
                <div className="aspect-[3/4] overflow-hidden">
                  <img src={c.img} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="mt-5">
                  <div className="flex items-center gap-2 text-secondary">
                    <MapPin className="w-4 h-4" />
                    <span className="overline text-[10px]">South Africa</span>
                  </div>
                  <h3 className="mt-2 font-serif text-2xl">{c.name}</h3>
                  <p className="mt-1 text-sm text-primary-foreground/70">{c.tag}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ WHY CAMPUSCO ============ */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-20 border-b border-border page-break">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="overline text-secondary">Why Campusco?</p>
            <h2 className="mt-3 font-serif text-4xl md:text-5xl leading-tight">
              A property firm students, parents and landlords <em className="italic text-secondary font-normal">actually recommend</em>.
            </h2>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-6">
            {[
              { title: "Woman-Led", body: "Our CEO and CFO are both women — leadership that reflects the country we serve." },
              { title: "B-BBEE Level 100%", body: "Fully compliant contributor recognised by South African transformation standards." },
              { title: "PPRA Registered", body: "Formal industry accreditation with all required Fidelity Fund Certificates." },
              { title: "NSFAS Support", body: "We help landlords navigate NSFAS accreditation — so your building qualifies for funded students." },
              { title: "25+ Years Experience", body: "A leadership team with a quarter-century of combined property management expertise." },
              { title: "Nationwide Footprint", body: "Boots on the ground in Johannesburg, Pretoria, Cape Town, Durban and beyond." },
            ].map((b) => (
              <div key={b.title} className="border-l-2 border-secondary pl-4">
                <p className="overline text-secondary text-[10px]">Trust</p>
                <p className="mt-1 font-serif text-xl">{b.title}</p>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{b.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FOR STUDENTS / FOR LANDLORDS ============ */}
      <section className="bg-muted/40 border-b border-border py-20 page-break">
        <div className="max-w-6xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-6">
          <div className="bg-background border border-border p-10">
            <HeartHandshake className="w-8 h-8 text-secondary" strokeWidth={1.5} />
            <p className="mt-5 overline text-secondary">For Students &amp; Parents</p>
            <h3 className="mt-3 font-serif text-3xl">Somewhere you&apos;ll actually call home.</h3>
            <ul className="mt-6 space-y-3 text-sm text-muted-foreground">
              <li className="flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-secondary" /> Fully-furnished suites with private or shared bathrooms.</li>
              <li className="flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-secondary" /> All-inclusive rentals — WiFi, utilities and cleaning covered.</li>
              <li className="flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-secondary" /> 24/7 access-controlled buildings with CCTV and biometric entry.</li>
              <li className="flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-secondary" /> Backup power &amp; water — no disruption during load-shedding.</li>
              <li className="flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-secondary" /> NSFAS accredited residences available.</li>
            </ul>
            <Button asChild className="mt-8 rounded-sm bg-primary text-primary-foreground h-11" data-testid="brochure-students-cta">
              <Link to="/leasing">Browse Accommodation</Link>
            </Button>
          </div>
          <div className="bg-primary text-primary-foreground p-10">
            <ShieldCheck className="w-8 h-8 text-secondary" strokeWidth={1.5} />
            <p className="mt-5 overline text-secondary">For Landlords &amp; Funders</p>
            <h3 className="mt-3 font-serif text-3xl">A partner your board can trust.</h3>
            <ul className="mt-6 space-y-3 text-sm text-primary-foreground/85">
              <li className="flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-secondary" /> Residence management, letting, collections, maintenance.</li>
              <li className="flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-secondary" /> Property evaluations and yield analysis.</li>
              <li className="flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-secondary" /> QS, architectural and structural engineering services.</li>
              <li className="flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-secondary" /> NSFAS accreditation guidance.</li>
              <li className="flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-secondary" /> Flexible fee structures for landlords and funders.</li>
            </ul>
            <Button asChild className="mt-8 rounded-sm bg-secondary text-secondary-foreground hover:bg-secondary/90 h-11" data-testid="brochure-landlords-cta">
              <Link to="/services">Explore Services</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* ============ CONTACT ============ */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-20">
        <div className="bg-primary text-primary-foreground p-10 md:p-16 relative overflow-hidden">
          <div className="absolute -right-16 -bottom-16 w-72 h-72 rounded-full bg-secondary/20 blur-3xl" />
          <div className="relative">
            <p className="overline text-secondary">Rise to your future</p>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl max-w-2xl leading-tight">
              Ready to see it in person?
            </h2>
            <p className="mt-4 max-w-xl text-primary-foreground/80">
              Book a viewing, request our full brochure, or drop us a note. Our team responds within one business day.
            </p>
            <div className="mt-10 grid sm:grid-cols-2 gap-8 text-sm max-w-3xl">
              <div>
                <p className="overline text-primary-foreground/60 text-[10px]">Enquiries</p>
                <p className="mt-2 font-serif text-2xl">info@thecampusco.co.za</p>
                <p className="mt-1 text-primary-foreground/70">060 836 2116</p>
                <p className="mt-1 text-primary-foreground/70">www.thecampusco.co.za</p>
              </div>
              <div>
                <p className="overline text-primary-foreground/60 text-[10px]">Head Office</p>
                <p className="mt-2 font-serif text-2xl">Bryanston</p>
                <p className="mt-1 text-primary-foreground/70">2 Bruton Road, Bryanston, 2191<br />Johannesburg, South Africa</p>
              </div>
            </div>
            <div className="mt-10 flex flex-wrap gap-3 no-print">
              <Button asChild className="rounded-sm bg-secondary text-secondary-foreground hover:bg-secondary/90 h-12 px-6">
                <Link to="/book-appointment">Book Appointment</Link>
              </Button>
              <Button asChild variant="outline" className="rounded-sm border-primary-foreground/40 text-primary-foreground hover:bg-primary-foreground hover:text-primary h-12 px-6">
                <Link to="/contact">Contact Us</Link>
              </Button>
            </div>
          </div>
        </div>
        <p className="mt-8 text-xs text-muted-foreground text-center">© {new Date().getFullYear()} Campusco (Pty) Ltd. All rights reserved. B-BBEE Level 100% · PPRA Registered · Woman-Led Enterprise.</p>
      </section>
    </div>
  );
}
